import React, { useContext, useEffect, useState } from 'react'
import { Grid, makeStyles, Typography } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import { Context as PortfolioContext } from '../contexts/portfolio'
import checkedCopy from '../assets/images/checkedCopy.svg'
import HelpRounded from '@material-ui/icons/HelpRounded'
import copyThumb from '../assets/images/copy-new.svg'
import EmmalynImage from '../assets/images/Emmalyn.png'

const useStyles = makeStyles({
  subheading: {
    marginBottom: 10,
    fontSize: 14,
    color: '#474747',
    fontWeight: 600,
  },
  section: {
    padding: '10px',
    marginBottom: 10,
    background: 'white',
    border: '1px solid #DFE4EB',
    borderRadius: 5,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  marketContextContainer: {
    borderTop: 'unset',
    background: '#fff',
    margin: 'unset !important',
    width: '100% !important',
  },
  chartContainerMobile: {
    margin: 'auto',
    paddingBottom: '5px !important',
  },
  lgBanner: {
    height: '100%',
    width: '100%',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },
  toolTipMobile: {
    color: '#8D9CB1',
    margin: 'auto',
    fontSize: '20px !important',
    cursor: 'pointer',
    marginLeft: '5px',
    width: '20px !important',
    height: '20px !important',
  },
  head: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 'none',
  },
  mobileHead: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #DFE4EB',
  },
  noDataText: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 13.5,
    lineHeight: '20px',
    color: '#181818',
  },
  main: {
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D1D1 transparent',
    borderRadius: '3px',
    padding: 0,
    '&::-webkit-scrollbar': {
      width: 5,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D1D1',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#D1D1D1',
    },
  },
  mainMobile: {
    height: '250px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D1D1 transparent',
    borderRadius: '3px',
    padding: 0,
    '&::-webkit-scrollbar': {
      width: 5,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D1D1',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#D1D1D1',
    },
  },
  leftMobileSection: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flexStart',
    flexDirection: 'row',
  },
  emmalynImage: {
    width: '100%',
    height: '200px',
    display: 'flex',
    alignItems: 'center',
  },
  noteTitleMobile: {
    fontFamily:
      'Inter, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 1.5,
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 4,
    border: '1px solid #E6EAF0',
    background: '#FFFFFF',
    cursor: 'pointer',
  },
  copyButtonMobile: {
    height: 25,
    padding: '0 8px',
    boxSizing: 'border-box',
  },
  copyLabel: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '18px',
    letterSpacing: 0,
    color: '#1B3963',
  },
  quickNoteHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 5,
    marginBottom: 5,
    borderBottom: '1px solid #E8EBEF',
  },
  quickNoteTitle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '22px',
    letterSpacing: 0,
    color: '#181818',
  },
  quickNoteSubTitle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '22px',
    letterSpacing: 0,
    color: '#757575',
    marginLeft: 6,
  },
  quickNoteContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D1D1 transparent',
    paddingRight: 8,
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: 5,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D1D1',
      borderRadius: 3,
    },
  },
})

const NoteContext = ({ tickerId, textFontSize }) => {
  const classes = useStyles()
  const { state } = useContext(PortfolioContext)

  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [copied, setCopied] = useState(false)
  const [time, setTime] = useState(0)
  const firstNotesData = React.useMemo(() => {
    const notesData = state?.notesData
    if (Array.isArray(notesData)) return notesData[0]
    if (Array.isArray(notesData?.history)) return notesData.history[0]
    if (Array.isArray(notesData?.notes_history))
      return notesData.notes_history[0]
    if (Array.isArray(notesData?.note_history)) return notesData.note_history[0]
    if (Array.isArray(notesData?.signal_history))
      return notesData.signal_history[0]
    if (Array.isArray(notesData?.signals)) return notesData.signals[0]
    if (Array.isArray(notesData?.data)) return notesData.data[0]
    return notesData
  }, [state?.notesData])
  const longText = firstNotesData?.signal_overview ?? ''
  const words = longText ? longText.split(' ') : []
  const showCopyButton = currentWordIndex >= words.length
  const quickNoteTextStyle =
    !isMobile && textFontSize ? { fontSize: textFontSize } : undefined

  useEffect(() => {
    if (!longText) {
      setCurrentWordIndex(0)
      setTime(0)
      return
    }
    if (currentWordIndex < words.length) {
      if (time >= 5000) {
        setCurrentWordIndex(prevIndex => words.length)
      } else {
        const intervalId = setInterval(() => {
          setTime(prevIndex => prevIndex + 30)
          setCurrentWordIndex(prevIndex => prevIndex + 1)
        }, 30)
        return () => clearInterval(intervalId)
      }
    }
  }, [currentWordIndex, longText, time, words.length])

  useEffect(() => {
    setCurrentWordIndex(0)
    setTime(0)
  }, [longText])

  return (
    <section
      className={classes.section}
      style={isMobile ? { padding: '20px 16px' } : { width: '100%' }}
    >
      <div
        className={isMobile ? classes.mobileHead : classes.head}
        style={{ paddingBottom: isMobile ? '16px' : '0px' }}
      >
        {!isMobile && (
          <div className={classes.quickNoteHeader}>
            <div>
              <span className={classes.quickNoteTitle}>Quick Note</span>
              <span className={classes.quickNoteSubTitle}>
                (At Time Risk Was Identified)
              </span>
            </div>
            <span
              onClick={e => {
                if (!showCopyButton) return
                e.stopPropagation()
                const textToCopy = longText

                if (
                  navigator &&
                  navigator.clipboard &&
                  typeof navigator.clipboard.writeText === 'function' &&
                  window.isSecureContext
                ) {
                  navigator.clipboard
                    .writeText(textToCopy)
                    .then(() => {
                      setCopied(true)
                      setTimeout(() => setCopied(false), 1500)
                    })
                    .catch(err => {
                      console.error('Copy failed using Clipboard API:', err)
                    })
                } else {
                  const textArea = document.createElement('textarea')
                  textArea.value = textToCopy
                  textArea.style.position = 'fixed'
                  textArea.style.left = '-999999px'
                  textArea.style.top = '-999999px'
                  document.body.appendChild(textArea)
                  textArea.focus()
                  textArea.select()

                  try {
                    document.execCommand('copy')
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  } catch (err) {
                    console.error(
                      'Fallback: Copy failed using execCommand:',
                      err,
                    )
                  }

                  document.body.removeChild(textArea)
                }
              }}
              data-pdf-hide
              style={{
                visibility: showCopyButton ? 'visible' : 'hidden',
                pointerEvents: showCopyButton ? 'auto' : 'none',
              }}
            >
              <span className={classes.copyButton}>
                <img
                  className="copy-icon"
                  src={copied ? checkedCopy : copyThumb}
                  alt="Copy"
                  style={{ width: 16, height: 16 }}
                />
                <span className={classes.copyLabel}>Copy</span>
              </span>
            </span>
          </div>
        )}

        {isMobile && (
          <div className={classes.leftMobileSection}>
            <Typography className={classes.noteTitleMobile}>
              Emmalyn Insights
            </Typography>
            <HelpRounded
              className={classes.toolTipMobile}
              key="1"
              onClick={() => handleClickOpenTooltip()}
            />
            <span
              onClick={e => {
                if (!showCopyButton) return
                e.stopPropagation()
                navigator.clipboard.writeText(longText)
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              }}
              data-pdf-hide
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                margin: 'auto',
                fontSize: '1.8rem',
                cursor: 'pointer',
                marginLeft: '8px',
                marginTop: -3,
                height: '30px',
                visibility: showCopyButton ? 'visible' : 'hidden',
                pointerEvents: showCopyButton ? 'auto' : 'none',
              }}
            >
              <span
                className={`${classes.copyButton} ${classes.copyButtonMobile}`}
              >
                <img
                  className="copy-icon"
                  src={copied ? checkedCopy : copyThumb}
                  alt="Copy"
                  style={{ width: 16, height: 16 }}
                />
                <span className={classes.copyLabel}>Copy</span>
              </span>
            </span>
          </div>
        )}
      </div>
      {!isMobile && (
        <div className={classes.quickNoteContent} data-pdf-expand="scroll">
          <Typography className={classes.noDataText} style={quickNoteTextStyle}>
            {words.slice(0, currentWordIndex).join(' ')}
            <span
              className="cursor"
              style={{ opacity: currentWordIndex >= words.length ? 0 : 1 }}
            >
              &#10074;
            </span>
          </Typography>
        </div>
      )}

      {isMobile && (
        <Grid className={classes.marketContextContainer} container spacing={3}>
          <Grid
            className={
              isMobile ? classes.chartContainerMobile : classes.chartContainer
            }
            item
            // xs={6}
            sm={2}
          >
            <img
              src={EmmalynImage}
              alt="Emmalyn Image"
              style={{
                width: '100%',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </Grid>
          <Grid
            className={
              isMobile
                ? classes.newsListContainerMobile
                : classes.newsListContainer
            }
            item
            // xs={18}
            sm={10}
          >
            <div
              className={isMobile ? classes.mainMobile : classes.main}
              data-pdf-expand="scroll"
            >
              <Typography
                className={classes.noDataText}
                style={quickNoteTextStyle}
              >
                {words.slice(0, currentWordIndex).join(' ')}
                <span
                  className="cursor"
                  style={{ opacity: currentWordIndex >= words.length ? 0 : 1 }}
                >
                  &#10074;
                </span>
              </Typography>
            </div>
          </Grid>
        </Grid>
      )}
    </section>
  )
}

export default NoteContext
